import React from 'react'

import { At as PositionedText, Block, HLine, Sheet, VLine } from './_shared/positioned'

// Réplica do modelo "Certificado de Expurgo e Exaustão de Gases / Gas Free Certificate" da Biofix (A4), com as
// coordenadas em pt do modelo. Os campos em branco do modelo (horas, toneladas, metros cúbicos e exaustão) continuam em branco.
const FONT = "'Times New Roman', Times, 'Liberation Serif', serif"
const CALIBRI = "Calibri, Carlito, 'Segoe UI', Arial, sans-serif"
const SIZE = 10.5
const BIG = 11.45
const LEFT = 18.8
const RIGHT = 573.5
const COMPANY = 'BIOFIX AGRI LTDA'
const EXHAUSTION_YEAR = '2026'

const At = ({ size = SIZE, ...props }) => <PositionedText size={size} {...props} />

// Linha em branco sublinhada, como os "____" do modelo
const Blank = ({ width, gap = 0 }) => (
  <span
    style={{
      display: 'inline-block',
      width: `${width}pt`,
      margin: `0 ${gap}pt -1.2pt`,
      borderBottom: '0.8pt solid #000'
    }}
  />
)

const Underlined = ({ children }) => <span style={{ textDecoration: 'underline' }}>{children}</span>

// Reduz a fonte quando o texto é longo, para continuar cabendo na largura `width` (em pt) da célula
const AVERAGE_CHAR_WIDTH = 0.62
const MIN_SIZE = 4.5
const fit = (text, width, size) => Math.max(MIN_SIZE, Math.min(size, width / (text.length * AVERAGE_CHAR_WIDTH)))

const Header = ({ data }) => (
  <>
    <HLine x1={LEFT} x2={RIGHT} y={20.7} />
    <VLine x={LEFT} y1={20.7} y2={99.5} />
    <VLine x={101.7} y1={21.6} y2={84.5} />
    <VLine x={436.2} y1={21.6} y2={84.5} />
    <VLine x={572.5} y1={20.7} y2={99.5} />
    <HLine x1={437.1} x2={RIGHT} y={50.4} />
    <HLine x1={LEFT} x2={RIGHT} y={83.5} />
    <HLine x1={LEFT} x2={RIGHT} y={98.5} />
    <img
      src="/pdfs/biofix/logo.png"
      alt="Biofix Agri"
      style={{ position: 'absolute', left: '167.9pt', top: '21.4pt', width: '199.5pt', height: '55.5pt' }}
    />
    <At x={438.3} y={31.3} size={9.5}>
      O.S.
    </At>
    <At x={529} y={31}>
      {data.activityCode}
    </At>
    <At x={438.3} y={62.8} size={9.5}>
      Nº Comunicado
    </At>
    <At x={529} y={62.9}>
      {data.communicationNumber}
    </At>
    <At x={LEFT} y={110} w={RIGHT - LEFT} size={BIG} bold align="center">
      CERTIFICADO DE EXPURGO E EXAUSTÃO DE GASES
    </At>
    <At x={LEFT} y={126.6} w={RIGHT - LEFT} size={BIG} bold italic align="center">
      - &quot; GAS FREE CERTIFICATE&quot; -
    </At>
  </>
)

const Certification = ({ data }) => (
  <>
    <HLine x1={LEFT} x2={RIGHT} y={154.1} />
    <VLine x={LEFT} y1={154.1} y2={296.6} />
    <VLine x={572.5} y1={154.1} y2={296.6} />
    {[169.8, 185.6, 201.3, 217, 232.7, 248.4, 264.2, 295.6].map(y => (
      <HLine key={y} x1={LEFT} x2={RIGHT} y={y} />
    ))}
    {[
      [186.5, 202.2, [61.7, 149.3, 248.7, 436.2]],
      [218, 233.7, [318.9]],
      [249.4, 265.1, [64.5, 196.9, 248.7, 436.2]]
    ].map(([y1, y2, xs]) => xs.map(x => <VLine key={`${y1}-${x}`} x={x} y1={y1} y2={y2} />))}

    <At x={21.1} y={158.5}>
      A Empresa Credenciada: <b>{COMPANY}</b> certifica que,
    </At>
    <At x={21.2} y={173.5} size={BIG} italic>
      The Accredited Company
    </At>
    <At x={151.6} y={174.6} bold italic style={{ fontFamily: CALIBRI }}>
      {COMPANY}
    </At>
    <At x={251} y={174.6} italic style={{ fontFamily: CALIBRI }}>
      certifies that,
    </At>

    <At x={21.2} y={189} size={BIG}>
      às
    </At>
    <At x={171.6} y={188.5} size={BIG}>
      horas do dia
    </At>
    <At x={249.6} y={188.6} w={185.3} size={BIG} bold align="right">
      {data.datePt}
    </At>
    <At x={438.6} y={188.5} size={BIG}>
      , realizou tratamento
    </At>

    <At x={21.2} y={204.7} size={BIG} italic>
      at
    </At>
    <At x={179.7} y={204.2} size={BIG} italic>
      hours in
    </At>
    <At x={269.7} y={204.2} size={BIG} bold italic>
      {data.monthEn}
    </At>
    <At x={385.1} y={204.2} size={BIG} bold italic>
      {data.yearEn}
    </At>
    <At x={438.6} y={204.2} size={BIG}>
      was executed the
    </At>

    <At x={21.2} y={220} size={BIG}>
      fitossanitário no (s) porão (ões) número(s)
    </At>
    <At x={292.2} y={220} size={BIG}>
      ,
    </At>
    <At x={321.3} y={220.1} size={BIG} bold>
      {data.holdsPt}
    </At>
    <At x={21.2} y={235.7} size={BIG} italic>
      phytosanitary treatment in the hold (s) number (s),
    </At>
    <At x={321.3} y={235.7} size={BIG} bold italic>
      {data.holdsEn}
    </At>

    <At x={21.2} y={250.4} size={BIG}>
      do navio
    </At>
    <At x={66.6} y={251} size={fit(data.ship, 128, 9.5)} bold>
      {data.ship}
    </At>
    <At x={199.3} y={250.4} size={BIG}>
      , contendo
    </At>
    <At x={249.6} y={250.4} w={174.4} size={BIG} align="right">
      {data.tons}
    </At>
    <At x={427.1} y={250.3} size={BIG} bold italic>
      t
    </At>
    <At x={438.6} y={250.4} size={BIG}>
      de
    </At>
    <At x={480.6} y={252.3} size={fit(data.goodsPt, 90, 9.5)} bold>
      {data.goodsPt}
    </At>

    <At x={25.7} y={267.1} size={BIG} italic>
      from the vessel
    </At>
    <At x={118} y={267.5} size={fit(data.ship, 129, SIZE)} bold italic>
      {data.ship}
    </At>
    <At x={251.1} y={266.2} size={BIG} italic>
      shipped with
    </At>
    <At x={482.6} y={267} size={BIG} bold italic>
      ton (s) of
    </At>
    <At x={21.2} y={282.7} size={BIG} bold italic>
      {data.goodsEn}
    </At>
  </>
)

const Treatment = ({ data }) => (
  <>
    <HLine x1={LEFT} x2={RIGHT} y={311.3} />
    <HLine x1={LEFT} x2={RIGHT} y={327.1} />
    <VLine x={LEFT} y1={311.3} y2={328} />
    <VLine x={572.5} y1={311.3} y2={328} />
    <VLine x={196.9} y1={311.3} y2={328} />
    <VLine x={361.1} y1={311.3} y2={328} />
    <At x={21.1} y={315.7}>
      O tratamento foi realizado com
    </At>
    <At x={196.9} y={315.7} w={164.2} bold align="center">
      FOSFINA
    </At>
    <At x={363.4} y={315.7}>
      na dosagem de
    </At>
    <At x={40.6} y={331.5} italic>
      The treatment was realized with
    </At>
    <At x={199.2} y={331.5} bold italic>
      PHOSPHINE
    </At>
    <At x={321.2} y={331.5} italic>
      in proporcion to
    </At>

    <HLine x1={LEFT} x2={RIGHT} y={342.8} />
    <HLine x1={LEFT} x2={RIGHT} y={358.5} />
    <VLine x={LEFT} y1={342.8} y2={359.5} />
    <VLine x={572.5} y1={342.8} y2={359.5} />
    {[149.3, 361.1, 436.2].map(x => (
      <VLine key={x} x={x} y1={343.7} y2={359.5} />
    ))}
    <At x={21.2} y={347.2} w={40.1} bold align="right">
      {data.dosage}
    </At>
    <At x={64} y={347.2} bold>
      grama por
    </At>
    <At x={149.3} y={347.2} w={212.8} align="center">
      metros cúbicos, com a duração de
    </At>
    <At x={362.1} y={347.2} w={74.1} align="center">
      {data.hours}
    </At>
    <At x={438.5} y={347.2}>
      horas,
    </At>
    <At x={21.2} y={361.6} w={40.1} size={BIG} bold italic align="right">
      {data.dosage}
    </At>
    <At x={64.1} y={361.6} size={BIG} bold italic>
      gram per
    </At>
    <At x={149.3} y={361.6} w={212.8} size={BIG} italic align="center">
      cubic meter, during
    </At>
    <At x={362.1} y={361.6} w={74.1} size={BIG} bold italic align="center">
      {data.hours}
    </At>
    <At x={438.6} y={361.6} size={BIG} italic>
      hours,
    </At>

    <HLine x1={LEFT} x2={RIGHT} y={374.2} />
    <HLine x1={LEFT} x2={RIGHT} y={389.9} />
    <HLine x1={LEFT} x2={RIGHT} y={405.7} />
    <VLine x={LEFT} y1={374.2} y2={406.6} />
    <VLine x={572.5} y1={374.2} y2={406.6} />
    <At x={21.1} y={378.6}>
      sob a supervisão do fiscal do Ministério da Agricultura, Pecuária e Abastecimento.
    </At>
    <At x={21.1} y={394.4} italic>
      under supervision of the Ministry of Agriculture, Livestock and Food Supply inspector.
    </At>
  </>
)

const Exhaustion = () => (
  <>
    <HLine x1={LEFT} x2={RIGHT} y={417.1} />
    <HLine x1={LEFT} x2={RIGHT} y={491.5} />
    <HLine x1={LEFT} x2={RIGHT} y={549.1} />
    <VLine x={LEFT} y1={417.1} y2={550.1} />
    <VLine x={572.5} y1={417.1} y2={550.1} />
    <At x={21.1} y={419.8} bold>
      Considerando a necessidade da partida do navio, que não pode permanecer atracado ou ao largo aguardando a
      expiração
    </At>
    <At x={21.1} y={433} bold>
      do tempo de exposição do gás, deve o Comandante da Embarcação realizar a exaustão do (s) porão (ões) em viagem,
    </At>
    <At x={21.1} y={446.2} bold>
      somente após às
      <Blank width={60.7} />
      horas do dia
      <Blank width={37} />
      de
      <Blank width={78} />
      de <Underlined>{EXHAUSTION_YEAR}</Underlined>, observando as NORMAS TÉCNICAS
    </At>
    <At x={21.1} y={459.4} bold>
      PARA A EFICIÊNCIA DO TRATAMENTO E SEGURANÇA DA TRIPULAÇÃO.
    </At>
    <At x={21.1} y={494.6} italic>
      Considering the necessity of the vessel departure, that can not remain&nbsp; alongside or offshore waiting for the
    </At>
    <At x={21.1} y={509} italic>
      time expiration of the gas exposition, the Vessel Captain must carry out the hold (s) exhaustion in trip, only
    </At>
    <At x={21.1} y={523.4} italic>
      after
      <Blank width={66} gap={1.9} />
      (time) on <Blank width={83.2} />, <Blank width={37} gap={1.9} />, <Underlined>{EXHAUSTION_YEAR}</Underlined>{' '}
      (date), caring for the TECHNICAL
    </At>
    <At x={21.1} y={537.8} italic>
      REGULATIONS FOR THE TREATMENT EFFICIENCY AND THE CREW SECURITY
    </At>
  </>
)

const Footer = ({ data }) => (
  <>
    <At x={21.1} y={591}>
      Local/Data: Place/Date:
    </At>
    <At x={129.8} y={591.7} w={187.9} size={9.5} align="right">
      {data.port}
    </At>
    <At x={321.2} y={591.7} size={9.5} style={{ fontFamily: "Arial, Helvetica, 'Liberation Sans', sans-serif" }}>
      - Brasil ................./........................................../.................
    </At>

    <HLine x1={LEFT} x2={RIGHT} y={616.7} />
    <HLine x1={LEFT} x2={RIGHT} y={738.8} />
    <VLine x={LEFT} y1={616.7} y2={739.8} />
    <VLine x={572.5} y1={616.7} y2={739.8} />
    <At x={LEFT} y={698.1} w={231.2} bold align="center">
      COMANDANTE DA EMBARCAÇÃO
    </At>
    <At x={LEFT} y={712.2} w={231.2} bold italic align="center">
      VESSEL CAPTAIN
    </At>
    <At x={249.6} y={698.1} w={322.9} bold align="center">
      RESPONSÁVEL TÉCNICO - Empresa Credenciada
    </At>
    <At x={249.6} y={712.1} w={322.9} italic align="center">
      Accredited Company
    </At>
  </>
)

const GasFreeDocument = ({ data }) => (
  <div id="pdfPage" style={{ width: '595.2pt', background: '#fff', fontFamily: FONT, color: '#000' }}>
    <style>{`@page { size: A4; margin: 0 }`}</style>
    <Sheet left={0} style={{ fontSize: `${SIZE}pt` }}>
      <Block top={0} height={790} plain>
        <Header data={data} />
        <Certification data={data} />
        <Treatment data={data} />
        <Exhaustion />
        <Footer data={data} />
      </Block>
    </Sheet>
  </div>
)

export default GasFreeDocument
